import React, { useEffect } from "react";
import styled from "styled-components";

// Styled component for the features section
const FeaturesSection = styled.section`
  background-color: #f8fafc; /* brandColors.secondary */
  padding: 120px 0;
  position: relative;

  .container {
    text-align: center;
  }

  .badge {
    background-color: rgba(59, 130, 246, 0.15); /* brandColors.accent with opacity */
    color: #3b82f6; /* brandColors.accent */
    border: 1px solid rgba(59, 130, 246, 0.3);
    padding: 8px 16px;
    border-radius: 20px;
    font-size: 0.95rem;
    font-weight: 500;
  }

  h2 {

    font-size: 60px;
    font-weight: 500;
    line-height: 1.2;
    letter-spacing: -0.32px;
    display: block;
    font-weight: bold;
  

  }

  p {
    font-size: clamp(1.1rem, 2.5vw, 1.2rem);
    color: #6b7280; /* brandColors.mediumGrey */
    max-width: 600px;
    margin: 0 auto 30px;
  }

  .modules-container {
    display: grid;
    grid-template-columns: 1fr 1fr; /* Split into two equal columns */
    gap: 40px;
    align-items: center;

    @media (max-width: 992px) {
      grid-template-columns: 1fr; /* Stack vertically on smaller screens */
      gap: 20px;
    }
  }

  .image-container {
    width: 100%;
    height: 100%;
    max-height: 500px; /* Adjust as needed */
    overflow: hidden;
    border-radius: 8px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);

    img {
      width: 100%;
      height: 100%;
      object-fit: cover; /* Ensure the image covers the container */
      display: block;
    }
  }

  .content-container {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .card {
    background-color: rgba(59, 130, 246, 0.08); /* brandColors.accent with opacity */
    border: 2px solid rgba(59, 130, 246, 0.2);
    border-radius: 8px;
    padding: 20px;
    text-align: left; /* Align text to the left for better layout */
    transition: transform 0.3s, box-shadow 0.3s;

    &:hover {
      transform: translateY(-8px);
      box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
    }
  }

  .module-item {
    display: flex;
    align-items: center;
    gap: 15px; /* Space between icon and title */
    margin-bottom: 15px;
  }

  .module-content {
    margin-bottom: 15px;
  }

  h3 {
    font-size: 1.1rem;
    color: #3b82f6; /* brandColors.accent */
    font-weight: 600;
    margin: 0; /* Remove default margin */
  }

  p {
    font-size: 0.9rem;
    color: #6b7280; /* brandColors.mediumGrey */
    margin: 5px 0 0; /* Add small top margin for spacing */
  }

  .icon-container {
    width: 30px;
    height: 30px;
    background-color: #ffffff; /* brandColors.white */
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    flex-shrink: 0; /* Prevent icon from shrinking */
  }
`;

function Features(props) {
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: "0px 0px -50px 0px",
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("animate-in");
        }
      });
    }, observerOptions);

    const animatedElements = document.querySelectorAll(".fade-in-up");
    animatedElements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const modules = [
    {
      title: "QUOTATIONS",
      description:
        "Create, customize and manage professional quotations with advanced templates and automated calculations.",
      icon: "fa-file-alt",
    },
    {
      title: "CLAIMS",
      description:
        "Efficiently process, track and manage insurance claims with automated workflows and status updates.",
      icon: "fa-check-circle",
    },
    {
      title: "ACCOUNTS",
      description:
        "Comprehensive financial management with detailed reporting, analytics and automated accounting processes.",
      icon: "fa-chart-bar",
    },
    {
      title: "APPOINTMENTS",
      description:
        "Seamlessly schedule, manage and track client appointments with automated reminders and calendar integration.",
      icon: "fa-calendar-alt",
    },
  ];

  return (
    <FeaturesSection id="features" className="fade-in-up">
      <div className="container">
        <div className="row justify-content-center mb-5">
          <div className="col-lg-12 #d-flex justify-content-center">
            <h2 className="text-center">Monitor your insurance business in one place</h2>
            <span className="text-center pt-2 inline-block">
              Streamline your business operations<br/> with our comprehensive suite of
              professional modules.
            </span>
          </div>
        </div>

        <div className="modules-container">
          <div className="image-container">
            <img
              src="https://images.unsplash.com/photo-1590178828415-4edc35b8faac?q=80&w=1636&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
              alt="Platform Modules Illustration"
            />
          </div>
          <div className="content-container">
            <div className="card">
              {modules.map((module, index) => (
                <div key={index} className="module-content">
                  <div className="module-item">
                    <div className="icon-container">
                      <i
                        className={`fas ${module.icon}`}
                        style={{ fontSize: "1.0rem" }}
                      />
                    </div>
                    <h3>{module.title}</h3>
                  </div>
                  <p>{module.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </FeaturesSection>
  );
}

export default Features;